// Import dependencies
import { html, LitElement } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Import definitions
import { getUniversalToken } from '@automator/builder/definitions/items/token';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-item-token-edit-dialog' )
export class AutomatorBuilderItemTokenEditDialog extends LitElement {
	// Properties
	// The token code
	@property( { type: String, attribute: 'token-code' } ) accessor tokenCode;

	// The code of the integration
	@property( { type: String, attribute: 'integration-code' } ) accessor integrationCode;

	// The condition fields
	@property( { type: Object, attribute: false } ) accessor fields = {};

	// The item ID
	@property( { type: String, attribute: 'item-id' } ) accessor itemID;

	// Returns the component that has the fields of this filter condition
	// We'll use it to retrieve the values
	@query( 'uap-builder-item-legacy-form' ) accessor $optionsForm;

	/**
	 * Disable the shadow DOM
	 *
	 * @todo Remove this method once fields become web components. This component should be using the shadow DOM.
	 * @return {Element} The element
	 */
	createRenderRoot() {
		return this;
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<uo-dialog
				heading=${ escapeHTML( __( 'Token data', 'uncanny-automator' ) ) }

				size="large"
				force-manual-close
				open

				@uap-dialog-close=${ this.handleDialogClose }
			>
				${ this._templateFields }

				<uo-button
					slot="primary-action"

					@click=${ this.handleConfirmEdit }
				>
					${ escapeHTML( _x( 'Confirm', 'Verb', 'uncanny-automator' ) ) }
				</uo-button>

				<uo-button
					slot="secondary-action"
					dialog-action="close"
					color="secondary"
				>
					${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
				</uo-button>
			</uo-dialog>
		`;
	}

	/**
	 * Returns the fields of the template
	 *
	 * @return {TemplateResult} The TemplateResult of the fields
	 */
	get _templateFields() {
		return html`
			<uap-builder-item-legacy-form
				item-type="token"
				item-id=${ ifDefined( this.itemID ) }

				.fieldsDirectory=${ this.directoryItemData.fields }
				.fieldsValues=${ this.fields }

				return-format="token"

				@uap-builder-legacy-form-change=${ ( event ) => {
					// Update the fields, even if they are not saved yet
					// The cancel button will revert the changes, and the save button will save them
					this.fields = event.detail.unsavedFieldValues;
				} }
			></uap-builder-item-legacy-form>
		`;
	}

	/**
	 * Handles updating the filter data on the server
	 *
	 * @return {Promise|undefined} The promise that either updates or creates the filter condition
	 */
	handleConfirmEdit() {
		// Check if the fields are available
		if ( isEmpty( this.$optionsForm ) ) {
			return;
		}

		// Validate the form
		if ( ! this.$optionsForm.areFieldsValid ) {
			return;
		}

		// Dispatch save event
		this.dispatchEvent( new CustomEvent(
			'uap-builder-item-token-saved-successfully',
			{
				detail: {
					unsavedFieldValues: this.$optionsForm.unsavedFieldValues
				}
			}
		) );

		// Delete this component
		this.handleDialogClose();
	}

	/**
	 * Returns the global/directory data of this item
	 *
	 * @return {Object} The directory data of this item. This contains, for example, the fields attributes
	 */
	get directoryItemData() {
		return getUniversalToken(
			this.integrationCode,
			this.tokenCode
		);
	}

	/**
	 * Returns the global/directory data of the integration of this filter condition
	 *
	 * @return {Object} The directory data of this filter condition. Contains, for example, the name of the integration.
	 */
	get directoryIntegrationData() {
		return getIntegration( this.integrationCode );
	}

	/**
	 * Delete this component when something closes the dialog
	 */
	handleDialogClose() {
		this.remove();
	}
}